import React, { useState, useEffect } from 'react';
import manualUpdateAPI from '../../services/manualUpdateAPI';
import { CARRIER_NAMES } from '../../utils/constants';
import { formatDateTime } from '../../utils/helpers';
import LoadingSpinner from '../common/LoadingSpinner';
import { decodeJWT } from '../../utils/jwt';

const UpdateHistory = () => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');

  useEffect(() => {
    loadUpdateHistory();
    // eslint-disable-next-line
  }, []);

  const loadUpdateHistory = async () => {
    try {
      // If companyId is needed for future API, decode agent here
  // const token = sessionStorage.getItem('agentToken');
      // const agent = decodeJWT(token);
      const response = await manualUpdateAPI.getAgentHistory();
      setUpdates(response.data);
    } catch (error) {
      console.error('Error loading update history:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredUpdates = updates.filter(update => {
    if (filter === 'ALL') return true;
    return update.carrierName === filter;
  });

  const carrierOptions = [
    { value: 'ALL', label: 'All Carriers' },
    ...Object.entries(CARRIER_NAMES).map(([value, label]) => ({
      value,
      label
    }))
  ];

  if (loading) {
    return <LoadingSpinner text="Loading update history..." />;
  }

  return (
    <div className="update-history">
      <div className="history-header">
        <h3>My Update History</h3>
        <p>Track all manual updates you've made to container statuses</p>
      </div>

      <div className="history-controls">
        <div className="filter-control">
          <label>Filter by Carrier:</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            {carrierOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="stats">
          <span>Total Updates: {updates.length}</span>
          <span>Showing: {filteredUpdates.length}</span>
        </div>
      </div>

      <div className="updates-list">
        {filteredUpdates.length > 0 ? (
          filteredUpdates.map(update => (
            <div key={update.id} className="update-card">
              <div className="update-header">
                <div className="container-info">
                  <span className="container-number">
                    {update.container ? update.container.containerNumber : 'N/A'}
                  </span>
                  <span className="booking-number">
                    {update.container ? update.container.bookingNumber : 'N/A'}
                  </span>
                </div>
                <div className="update-meta">
                  <span className="carrier-badge">
                    {CARRIER_NAMES[update.carrierName]}
                  </span>
                  <span className="timestamp">
                    {formatDateTime(update.systemUpdateTime)}
                  </span>
                </div>
              </div>

              <div className="update-content">
                <div className="status-change">
                  <div className="original-status">
                    <strong>Carrier Status:</strong> {update.originalCarrierText}
                  </div>
                  <div className="arrow">→</div>
                  <div className="mapped-status">
                    <strong>Mapped To:</strong> {update.internalStatus}
                  </div>
                </div>

                <div className="customer-display">
                  <strong>Customer Sees:</strong> "{update.customerDisplayText}"
                </div>

                <div className="event-details">
                  <div className="detail">
                    <strong>Location:</strong> {update.eventLocation}
                  </div>
                  <div className="detail">
                    <strong>Event Time:</strong> {formatDateTime(update.carrierEventTime)}
                  </div>
                  {update.notes && (
                    <div className="detail">
                      <strong>Notes:</strong> {update.notes}
                    </div>
                  )}
                </div>
              </div>

              <div className="update-footer">
                <span className="ip-address">
                  IP: {update.ipAddress || 'N/A'}
                </span>
              </div>
            </div>
          ))
        ) : (
          <div className="empty-state">
            <p>No updates found</p>
            <p>Your manual status updates will appear here</p>
          </div>
        )}
      </div>

  <style>{`
  .update-history {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          padding: 2rem;
        }
        
        .history-header {
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #eee;
        }
        
        .history-header h3 {
          margin: 0 0 0.5rem 0;
          color: #333;
        }
        
        .history-header p {
          margin: 0;
          color: #666;
        }
        
        .history-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          padding: 1rem;
          background: #f8f9fa;
          border-radius: 6px;
        }
        
        .filter-control {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        
        .filter-control label {
          font-weight: 500;
          color: #555;
        }
        
        .filter-control select {
          padding: 0.5rem;
          border: 1px solid #ddd;
          border-radius: 4px;
          min-width: 150px;
        }
        
        .stats {
          display: flex;
          gap: 1.5rem;
          color: #666;
          font-size: 0.9rem;
        }
        
        .updates-list {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        
        .update-card {
          border: 1px solid #e9ecef;
          border-radius: 8px;
          overflow: hidden;
          transition: all 0.3s;
        }
        
        .update-card:hover {
          border-color: #007bff;
          box-shadow: 0 2px 8px rgba(0, 123, 255, 0.1);
        }
        
        .update-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
          background: #f8f9fa;
          border-bottom: 1px solid #e9ecef;
        }
        
        .container-info {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        
        .container-number {
          font-weight: 600;
          color: #333;
          font-size: 1.1rem;
        }
        
        .booking-number {
          color: #666;
          background: white;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.8rem;
        }
        
        .update-meta {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        
        .carrier-badge {
          background: #007bff;
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 12px;
          font-size: 0.8rem;
          font-weight: 500;
        }
        
        .timestamp {
          color: #666;
          font-size: 0.9rem;
        }
        
        .update-content {
          padding: 1.5rem;
        }
        
        .status-change {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
          padding: 1rem;
          background: #f8f9fa;
          border-radius: 6px;
        }
        
        .original-status, .mapped-status {
          flex: 1;
        }
        
        .arrow {
          color: #007bff;
          font-weight: bold;
          font-size: 1.2rem;
        }
        
        .customer-display {
          margin-bottom: 1rem;
          padding: 1rem;
          background: #e7f3ff;
          border-radius: 6px;
          border-left: 4px solid #007bff;
        }
        
        .event-details {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }
        
        .detail {
          padding: 0.5rem;
          background: white;
          border: 1px solid #e9ecef;
          border-radius: 4px;
        }
        
        .update-footer {
          padding: 0.75rem 1.5rem;
          background: #f8f9fa;
          border-top: 1px solid #e9ecef;
          text-align: right;
        }
        
        .ip-address {
          color: #666;
          font-size: 0.8rem;
        }
        
        .empty-state {
          text-align: center;
          padding: 3rem;
          color: #666;
        }
        
        .empty-state p {
          margin: 0.5rem 0;
        }
        
        @media (max-width: 768px) {
          .history-controls {
            flex-direction: column;
            gap: 1rem;
            align-items: stretch;
          }
          
          .filter-control {
            justify-content: space-between;
          }
          
          .update-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
          }
          
          .status-change {
            flex-direction: column;
            text-align: center;
          }
          
          .event-details {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default UpdateHistory;